import React, { useState } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { CalendarClock, GraduationCap, Pencil, Trash2, X } from "lucide-react";
import TabsCst from "@components/TabsCst";
import {
  MEETING_SETTINGS,
  MeetingSettingField,
  SettingSection,
} from "../SessionWizard/config/meetingSettings";
import { MeetingTool } from "../SessionWizard/types";
import { personLabel } from "../SessionWizard/editMapping";
import { EditionKind, EditionSession } from "../types";
import SessionInvitesTab from "./SessionInvitesTab";

interface SessionDetailModalProps {
  session: EditionSession;
  kind: EditionKind;
  corporateId: string | number;
  editionId: string | number;
  sessionNumber: number;
  contextLabel?: string;
  initialTab?: DetailTab;
  onEditSession?: () => void;
  onOpenReschedule?: () => void;
  onDeleteSession?: () => void;
  deleteDisabled?: boolean;
  onClose: () => void;
}

export type DetailTab = "details" | "verify-invites";

const SETTINGS_SECTIONS: SettingSection[] = [
  "GENERAL",
  "LOBBY",
  "ROLES",
  "INVITE",
];

const initials = (label?: string): string =>
  (label || "")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const getSessionValue = (session: any, key: string): any => {
  if (key === "coorganizer") {
    return session.coorganizer ?? session.co_organizer;
  }
  if (key === "trigger_external_services") {
    return (
      session.trigger_external_services === true ||
      !!session.online_meeting_updated_at
    );
  }
  return session[key];
};

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  session,
  kind,
  corporateId,
  editionId,
  sessionNumber,
  contextLabel,
  initialTab = "details",
  onEditSession,
  onOpenReschedule,
  onDeleteSession,
  deleteDisabled = false,
  onClose,
}) => {
  const { t } = useTranslation(["session-detail", "session-wizard"]);
  const theme = useTheme();

  const [tab, setTab] = useState<DetailTab>(initialTab);

  const isVirtual = kind === "virtual";
  const tool = (session.tool as MeetingTool | undefined) || undefined;

  const tabs = [
    { key: "details", label: String(t("session-detail:tabs.details")) },
    {
      key: "verify-invites",
      label: String(t("session-detail:tabs.verify-invites")),
    },
  ];

  const sectionLabel = (
    icon: React.ReactNode,
    title: string,
    action?: React.ReactNode,
  ) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {icon}
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  const fieldLabel = (text: string) => (
    <Typography sx={{ fontSize: "12px", color: theme.palette.text.primary }}>
      {text}
    </Typography>
  );

  const valueText = (text: string) => (
    <Typography sx={{ fontSize: "14px", color: theme.palette.text.primary }}>
      {text}
    </Typography>
  );

  const readOnlyBox = (text: string) => (
    <Box
      sx={{
        padding: "8px 12px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        fontSize: "14px",
        color: theme.palette.text.primary,
      }}
    >
      {text}
    </Box>
  );

  const renderPeople = (people: any[] | null | undefined, role: string) =>
    (people || []).map((p) => (
      <Box
        key={String(p.id)}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: "13px",
            fontWeight: 700,
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          }}
        >
          {initials(personLabel(p))}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {personLabel(p)}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.primary }}
          >
            {role}
          </Typography>
        </Box>
      </Box>
    ));

  const renderSettingField = (field: MeetingSettingField) => {
    const value = getSessionValue(session, field.key);

    if (field.type === "switch") {
      return (
        <Box
          key={field.key}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t(`session-wizard:settings.${field.key}`)}
          </Typography>
          <Switch
            checked={value === true}
            onChange={() => undefined}
            sx={{ pointerEvents: "none" }}
          />
        </Box>
      );
    }

    if (field.type === "select" || field.type === "text") {
      const display =
        field.type === "select" && field.optionsKey && value
          ? String(t(`session-wizard:options.${field.optionsKey}.${value}`))
          : value || "-";
      return (
        <Box
          key={field.key}
          sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {fieldLabel(String(t(`session-wizard:settings.${field.key}`)))}
          {readOnlyBox(String(display))}
        </Box>
      );
    }

    const people = Array.isArray(value) ? value : [];
    return (
      <Box
        key={field.key}
        sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
      >
        {fieldLabel(String(t(`session-wizard:settings.${field.key}`)))}
        {valueText(people.map((p) => personLabel(p)).join(", ") || "-")}
      </Box>
    );
  };

  const renderSettingsSection = (sectionKey: SettingSection) => {
    if (!tool) return null;
    const fields = MEETING_SETTINGS[tool].filter(
      (f) => f.section === sectionKey,
    );
    if (fields.length === 0) return null;

    const switches = fields.filter((f) => f.type === "switch");
    const others = fields.filter((f) => f.type !== "switch");

    return (
      <Box
        key={sectionKey}
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {sectionLabel(
          null,
          String(t(`session-wizard:details.sections.${sectionKey}`)),
        )}
        {switches.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 3,
              rowGap: 1.5,
            }}
          >
            {switches.map(renderSettingField)}
          </Box>
        )}
        {others.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: 3,
              rowGap: 1.5,
            }}
          >
            {others.map(renderSettingField)}
          </Box>
        )}
      </Box>
    );
  };

  const dateTimeSection = () => {
    const start = session.start_date
      ? dayjs(session.start_date).locale("it")
      : null;
    const end = session.end_date ? dayjs(session.end_date).locale("it") : null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {sectionLabel(
          <CalendarClock size={15} color={theme.palette.text.tertiary} />,
          String(t("session-detail:datetime")),
          onOpenReschedule ? (
            <Box
              role="button"
              onClick={onOpenReschedule}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: theme.palette.primary.main,
                fontSize: "13px",
                fontWeight: 700,
              }}
            >
              <CalendarClock size={14} />
              {t("session-detail:reschedule")}
            </Box>
          ) : undefined,
        )}
        {start && (
          <Box>
            <Typography
              sx={{ fontSize: "14px", color: theme.palette.text.primary }}
            >
              {capitalize(start.format("dddd, D MMMM YYYY"))}
            </Typography>
            <Typography
              sx={{ fontSize: "13px", color: theme.palette.text.primary }}
            >
              {t("session-detail:time-range", {
                start: start.format("HH:mm"),
                end: end ? end.format("HH:mm") : "-",
                gmt: start.format("Z"),
              })}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const peopleSection = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {sectionLabel(
        <GraduationCap size={15} color={theme.palette.text.tertiary} />,
        String(t("session-detail:people")),
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        {renderPeople(
          session.faculties,
          String(t("session-detail:role-faculty")),
        )}
        {renderPeople(session.tutors, String(t("session-detail:role-tutor")))}
      </Box>
    </Box>
  );

  const divider = <Divider sx={{ borderColor: theme.palette.divider }} />;

  const renderDetails = () => {
    if (isVirtual) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {sectionLabel(null, String(t("session-detail:link")))}
            {valueText(session.url || session.link || "-")}
          </Box>
          {divider}
          {dateTimeSection()}
          {divider}
          {peopleSection()}
          {SETTINGS_SECTIONS.map((s) => {
            const content = renderSettingsSection(s);
            return content ? (
              <React.Fragment key={s}>
                {divider}
                {content}
              </React.Fragment>
            ) : null;
          })}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {dateTimeSection()}
        {divider}
        {peopleSection()}
        {divider}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {sectionLabel(null, String(t("session-detail:site")))}
          {valueText(session.site?.label || "-")}
        </Box>
        {divider}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {sectionLabel(null, String(t("session-detail:rooms")))}
          {valueText(
            (session.rooms || []).map((r) => r.label).join(", ") || "-",
          )}
        </Box>
        {divider}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("session-detail:qr-code")}
          </Typography>
          <Switch
            checked={(session as any)["QR-code"] === true}
            onChange={() => undefined}
            sx={{ pointerEvents: "none" }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ padding: "20px 24px 0" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              {contextLabel && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  <GraduationCap size={16} />
                  <Typography sx={{ fontSize: "13px" }}>
                    {contextLabel}
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mt: contextLabel ? 0.5 : 0,
                }}
              >
                {t("session-detail:session-title", { number: sessionNumber })}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                onClick={() => onEditSession?.()}
                aria-label="edit"
                sx={{ color: theme.palette.primary.main }}
              >
                <Pencil size={18} />
              </IconButton>
              <IconButton
                onClick={() => onDeleteSession?.()}
                disabled={deleteDisabled}
                aria-label="delete"
                sx={{
                  color: theme.palette.primary.main,
                  "&.Mui-disabled": { color: theme.palette.action.disabled },
                }}
              >
                <Trash2 size={18} />
              </IconButton>
              <IconButton
                onClick={onClose}
                aria-label="close"
                sx={{ color: theme.palette.text.primary }}
              >
                <X size={20} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TabsCst
              tabs={tabs}
              activeKey={tab}
              onChange={(k) => setTab(k as DetailTab)}
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 3, padding: "24px" }}>
          {tab === "details" ? (
            renderDetails()
          ) : (
            <SessionInvitesTab
              corporateId={corporateId}
              editionId={editionId}
              session={session}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SessionDetailModal;
